"use client";

import { ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useVisitorNewsPostCardQuery } from "@/lib/graphql/queries/generated/news.generated";
import { cacheKeys } from "@/lib/offline-cache";
import { useOfflineData } from "@/lib/use-offline-data";
import { useWidgetEnv } from "@/lib/use-widget-env";
import type { HomeBlock, WidgetTab } from "@/lib/widget-config";
import { useWidgetInstance } from "@/lib/widget-instance-context";
import { extractFeaturedPost } from "../helpers";
import { useLoadedImage } from "./use-loaded-image";

/** The client's theme gradient (the home header glow colour, falling back to the primary), darkened
 *  so the white headline stays readable. */
const FEATURED_GRADIENT =
  "linear-gradient(135deg, var(--wx-home-glow, var(--wx-primary)) 0%, color-mix(in srgb, var(--wx-home-glow, var(--wx-primary)) 55%, #000) 100%)";

/** Single hero card for a featured news post.
 *  - **Live mode**: `newsId` set → hydrate via `visitorNewsPost({ id })`,
 *    render the post's cover + title + excerpt with the operator's
 *    `ctaLabel` (or i18n default) underneath. No id / not-found →
 *    paint a gradient placeholder.
 *  - **Demo mode**: hardcoded Pioneer "Save the date" preview so the
 *    dashboard's demo toggle shows the populated look operators are
 *    designing toward.
 */
export function FeaturedArticleBlock({
  block,
  onOpenTab,
  onOpenNewsArticle,
  isDummy,
}: {
  block: Extract<HomeBlock, { kind: "featured-article" }>;
  onOpenTab: (tab: WidgetTab) => void;
  onOpenNewsArticle?: (id: string) => void;
  isDummy: boolean;
}) {
  const t = useTranslations("home");
  const env = useWidgetEnv();

  // Dashboard-prefetched lookup takes precedence — the preview pane
  // already has the operator-side post loaded, so we render that
  // instead of round-tripping through the visitor surface.
  const previewPost =
    !isDummy && block.newsId ? env.previewData?.newsPostsById?.[block.newsId] : undefined;
  const hasPreview = previewPost !== undefined;

  // Real-data path — `visitorNewsPost({ id })` returns the post or
  // `null` for unpublished / cross-integration / missing ids. Uses the
  // slim card-only selection (no `contentMarkdown` / joins); the full
  // post hydrates lazily inside the article-detail view.
  const { data, loading } = useVisitorNewsPostCardQuery({
    variables: { args: { id: block.newsId ?? "" } },
    skip: isDummy || hasPreview || !block.newsId,
  });
  // Offline READ: cache the featured post so the home hero still renders when
  // the BE is down. (This query is locale-less, so the key omits locale.)
  const featPk = useWidgetInstance().publicKey;
  const effectiveData = useOfflineData(
    featPk && !isDummy && !hasPreview && block.newsId
      ? cacheKeys.home(featPk, `news-card:${block.newsId}`, "_")
      : null,
    data,
  );

  if (isDummy) {
    return (
      <motion.button
        type="button"
        // The demo hero IS the Pioneer 2026 post (`demo-news-1`) — open
        // it directly so the CTA reads as "see this article", not "go
        // to the news list". Falls back to the news tab if the shell
        // didn't wire article navigation.
        onClick={() => (onOpenNewsArticle ? onOpenNewsArticle("demo-news-1") : onOpenTab("news"))}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.99 }}
        transition={{ type: "spring", stiffness: 360, damping: 26 }}
        className="block w-full overflow-hidden rounded-wx-xl bg-wx-bg-elevated text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
      >
        {/* Hero: gradient cover + centered eyebrow / headline / subline */}
        <div
          className="relative w-full"
          style={{
            aspectRatio: "16 / 11",
            backgroundImage: FEATURED_GRADIENT,
          }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-6 text-center text-white">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase opacity-80">
              {t("featuredArticleDemoEyebrow")}
            </p>
            <p className="text-2xl leading-tight font-bold">{t("featuredArticleDemoHeadline")}</p>
            <p className="text-sm opacity-85">{t("featuredArticleDemoSubline")}</p>
          </div>
        </div>
        {/* Footer CTA row — tonal background, separates from the hero */}
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <span className="truncate text-sm font-semibold text-wx-fg">
            {t("featuredArticleDemoCta")}
          </span>
          <ChevronRight size={16} className="shrink-0 text-wx-fg-subtle rtl:-scale-x-100" />
        </div>
      </motion.button>
    );
  }

  // ─── Live mode ───────────────────────────────────────────────
  // No id → placeholder. Loading / not-found → placeholder. Both
  // states render the gradient hero so the block is never blank.
  const post = extractFeaturedPost(hasPreview ? previewPost : effectiveData?.visitorNewsPost);
  const interactive = !!post;
  const ctaLabel = block.ctaLabel?.trim() || t("featuredArticleTap");
  const onClick = interactive
    ? () => (onOpenNewsArticle ? onOpenNewsArticle(post.id) : onOpenTab("news"))
    : undefined;
  const coverImage = post?.coverImageUrl;

  if (!block.newsId || (!loading && !post)) {
    return (
      <motion.button
        type="button"
        onClick={() => onOpenTab("news")}
        whileHover={{ y: -1 }}
        whileTap={{ scale: 0.99 }}
        transition={{ type: "spring", stiffness: 360, damping: 26 }}
        className="block w-full overflow-hidden rounded-wx-xl bg-wx-bg-elevated text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
      >
        <div
          className="relative w-full"
          style={{
            aspectRatio: "16 / 11",
            backgroundImage: FEATURED_GRADIENT,
          }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-6 text-center text-white">
            <p className="text-2xl leading-tight font-bold">
              {block.newsId ? t("featuredArticleLoading") : t("featuredArticleEmpty")}
            </p>
            <p className="text-sm opacity-80">{block.newsId ? "" : t("featuredArticleSetUp")}</p>
          </div>
        </div>
      </motion.button>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={interactive ? { y: -2 } : undefined}
      whileTap={interactive ? { scale: 0.99 } : undefined}
      transition={{ type: "spring", stiffness: 360, damping: 26 }}
      disabled={!interactive}
      className="block w-full overflow-hidden rounded-wx-xl bg-wx-bg-elevated text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
    >
      <FeaturedHero title={post?.title} excerpt={post?.excerpt} coverImage={coverImage} />
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <span className="truncate text-sm font-semibold text-wx-fg">{ctaLabel}</span>
        <ChevronRight size={16} className="shrink-0 text-wx-fg-subtle rtl:-scale-x-100" />
      </div>
    </motion.button>
  );
}

/** Hero: the theme gradient until the cover has really loaded (and for good if it fails); a loaded
 *  cover always gets a dark scrim so the white text keeps its contrast. */
function FeaturedHero({
  title,
  excerpt,
  coverImage,
}: {
  title?: string;
  excerpt?: string;
  coverImage?: string;
}) {
  const loaded = useLoadedImage(coverImage);
  return (
    <div
      className="relative w-full"
      data-cover={loaded ? "image" : "gradient"}
      style={{
        aspectRatio: "16 / 11",
        backgroundImage: loaded
          ? `linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.6) 100%), url(${loaded})`
          : FEATURED_GRADIENT,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-6 text-center text-white">
        <p className="text-2xl leading-tight font-bold">{title}</p>
        {excerpt && <p className="text-sm opacity-85 line-clamp-2">{excerpt}</p>}
      </div>
    </div>
  );
}
